import {Group, Select} from '@mantine/core';
import React from 'react';

import type {useStyles} from 'antibodies/schema-configuration/schema-migration/NewFieldsMigrationPreview';
import {ClientSchemaField} from 'registry/field/FieldTable';
import getIconAndLabel from 'registry/field/GetFieldLabelAndIcon';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

type MigrationClasses = ReturnType<typeof useStyles>['classes'];

function getFieldTypeLabel(
  field: ClientSchemaField,
  schemaFieldSelectors: Array<SchemaFieldSelector>,
  classes: MigrationClasses
): JSX.Element {
  const {icon, label} = getIconAndLabel({
    field,
    schemaFieldSelectors,
    inEditMode: false,
  });
  return (
    <Group spacing="xs" align="center" noWrap={true}>
      <BenchlingIcon icon={icon} className={classes.iconStyle} />
      {label}
    </Group>
  );
}

export interface NewFieldMigrationRowProps {
  destField: ClientSchemaField;
  isMigratable: boolean;
  sourceFieldId: string | null;
  matchingFields: Array<SchemaField | ClientSchemaField>;
  schemaFieldSelectors: Array<SchemaFieldSelector>;
  classes: MigrationClasses;
  onSourceFieldChange: (sourceFieldId: string | null) => void;
}

export default function NewFieldMigrationRow({
  destField,
  isMigratable,
  sourceFieldId,
  matchingFields,
  schemaFieldSelectors,
  classes,
  onSourceFieldChange,
}: NewFieldMigrationRowProps): JSX.Element {
  // Only fields with an apiId can be a Select option (the value is the apiId), so drop any without
  // one rather than asserting; disabled then reflects whether any selectable source actually remains.
  const sourceFieldOptions = matchingFields
    .filter((f): f is typeof f & {apiId: string} => f.apiId != null)
    .map((f) => ({value: f.apiId, label: f.displayName}));

  return (
    <tr>
      <td>{destField.name}</td>
      <td className={classes.definitionCell}>
        {getFieldTypeLabel(destField, schemaFieldSelectors, classes)}
      </td>
      <td>{destField.isRequired ? 'Required' : ''}</td>
      <td>
        {isMigratable && (
          <Select
            aria-label={`Source field for ${destField.name}`}
            data={sourceFieldOptions}
            value={sourceFieldId}
            onChange={(val) => {
              onSourceFieldChange(val);
            }}
            clearable={true}
            placeholder="None"
            disabled={sourceFieldOptions.length === 0}
          />
        )}
      </td>
    </tr>
  );
}
